'use client';
import { useQuery } from '@tanstack/react-query';

// Interfaces para los datos de Strapi
interface StrapiTextChild {
  text: string;
  type: string;
  bold?: boolean;
}

interface StrapiTextParagraph {
  type: string;
  children: StrapiTextChild[];
}

interface StrapiImage {
  id: number;
  name: string;
  url: string;
  formats?: {
    thumbnail?: { url: string };
    large?: { url: string };
    medium?: { url: string };
    small?: { url: string };
  };
}

interface StrapiHiperaliaInformacion {
  id: number;
  Titulo: string; // Es un string simple, no un array de párrafos
  Parrafo_Izquierda?: StrapiTextParagraph[];
  Parrafo_Derecha?: StrapiTextParagraph[];
  Imagen?: StrapiImage;
}

interface StrapiInformacionPorCategoria {
  id: number;
  Prestashop_ID_Categoria: number;
  Hiperalia_Informacion: StrapiHiperaliaInformacion;
}

interface StrapiFAQItem {
  id: number;
  Pregunta: string;
  Respuesta: string;
}

interface StrapiFAQPorCategoria {
  id: number;
  Prestashop_Nombre_Categoria: string;
  Prestashop_ID_Categoria: number;
  Preguntas_Frecuentes: StrapiFAQItem[];
}

interface StrapiProductListingData {
  id: number;
  documentId: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  Informacion_por_Categoria: StrapiInformacionPorCategoria[];
  Preguntas_Frecuentes_por_categoria: StrapiFAQPorCategoria[];
}

interface StrapiProductListingResponse {
  data: StrapiProductListingData;
  meta: any;
}

// Interfaces para los datos procesados
interface ProcessedHiperaliaInfo {
  id: number;
  categoryId: number;
  title: string; // Cambiado a string para coincidir con los datos reales de Strapi
  subtitle1: StrapiTextParagraph[];
  subtitle2: StrapiTextParagraph[];
  imagen?: StrapiImage;
}

interface ProcessedFAQInfo {
  id: number;
  categoryId: number;
  categoryName: string;
  faqs: StrapiFAQItem[];
}

interface ProcessedProductListingData {
  categoriesInfo: ProcessedHiperaliaInfo[];
  faqsByCategory: ProcessedFAQInfo[];
}

interface UseStrapiProductListingReturn {
  data: ProcessedProductListingData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  getCategoryInfo: (categoryId: number) => ProcessedHiperaliaInfo | null;
  getCategoryFAQs: (categoryId: number) => ProcessedFAQInfo | null;
}

// Función para validar y limpiar datos de texto de Strapi
const sanitizeStrapiText = (text: any): StrapiTextParagraph[] => {
  // Si es null, undefined o no es un array, retornar array vacío
  if (!text || !Array.isArray(text)) {
    console.warn('StrapiText no es un array válido:', text);
    return [];
  }
  
  // Validar que cada elemento del array tenga la estructura esperada
  return text.filter(paragraph => 
    paragraph && 
    typeof paragraph === 'object' && 
    paragraph.type && 
    paragraph.children && 
    Array.isArray(paragraph.children)
  );
};

const fetchStrapiProductListingData = async (): Promise<ProcessedProductListingData> => {
  const response = await fetch('/api/strapi/listado-de-producto', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `Error HTTP: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (jsonError) {
      console.error('Error parsing error response:', jsonError);
    }
    throw new Error(errorMessage);
  }

  let jsonData: StrapiProductListingResponse;
  try {
    jsonData = await response.json();
  } catch (jsonError) {
    console.error('Error parsing Strapi response:', jsonError);
    throw new Error('Error al parsear respuesta de Strapi');
  }

  if (jsonData.data) {
    const productListingData = jsonData.data;
    
    // Procesar información por categoría
    const categoriesInfo: ProcessedHiperaliaInfo[] = (productListingData.Informacion_por_Categoria || []).map(categoryInfo => ({
      id: categoryInfo.id,
      categoryId: categoryInfo.Prestashop_ID_Categoria,
      title: categoryInfo.Hiperalia_Informacion?.Titulo || '', // Tomar el string directamente
      subtitle1: sanitizeStrapiText(categoryInfo.Hiperalia_Informacion?.Parrafo_Izquierda),
      subtitle2: sanitizeStrapiText(categoryInfo.Hiperalia_Informacion?.Parrafo_Derecha),
      imagen: categoryInfo.Hiperalia_Informacion?.Imagen
    }));

    // Procesar preguntas frecuentes por categoría
    const faqsByCategory: ProcessedFAQInfo[] = (productListingData.Preguntas_Frecuentes_por_categoria || []).map(faqCategory => ({
      id: faqCategory.id,
      categoryId: faqCategory.Prestashop_ID_Categoria,
      categoryName: faqCategory.Prestashop_Nombre_Categoria,
      faqs: faqCategory.Preguntas_Frecuentes || []
    }));

    const processedData: ProcessedProductListingData = {
      categoriesInfo,
      faqsByCategory
    };

    return processedData;
  } else {
    throw new Error('No se encontraron datos en la respuesta');
  }
};

const useStrapiProductListing = (): UseStrapiProductListingReturn => {
  const {
    data,
    isLoading: loading,
    error,
    refetch
  } = useQuery<ProcessedProductListingData, Error>({
    queryKey: ['strapi-product-listing'],
    queryFn: fetchStrapiProductListingData,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Función para obtener información de una categoría específica
  const getCategoryInfo = (categoryId: number): ProcessedHiperaliaInfo | null => {
    if (!data?.categoriesInfo) return null;
    
    return data.categoriesInfo.find(info => info.categoryId === categoryId) || null;
  };

  // Función para obtener FAQs de una categoría específica
  const getCategoryFAQs = (categoryId: number): ProcessedFAQInfo | null => {
    if (!data?.faqsByCategory) return null;
    
    return data.faqsByCategory.find(faq => faq.categoryId === categoryId) || null;
  };

  return { 
    data: data || null, 
    loading, 
    error: error?.message || null, 
    refetch: () => refetch(),
    getCategoryInfo,
    getCategoryFAQs
  };
};

export default useStrapiProductListing;

// Exportar interfaces para uso externo
export type { StrapiFAQItem, ProcessedFAQInfo, ProcessedHiperaliaInfo, ProcessedProductListingData };
